/**
 * ImmersiveWorkout - Fullscreen immersive workout experience for web
 * Matches native app ImmersiveWorkoutScreen design
 */
import * as React from 'react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import styled from '@emotion/styled';
import { Theme } from '../../../../Theme/Theme';
import { useRootStore } from '../../../../Store/useRootStore';
import { useTrans } from '../../../../Store/System/LocalizationStore';
import { ImmersiveWorkoutHeader } from './ImmersiveWorkoutHeader';
import { ImmersiveWorkoutVideoBackground } from './ImmersiveWorkoutVideoBackground';
import { ImmersiveWorkoutCountdown } from './ImmersiveWorkoutCountdown';
import { ImmersiveWorkoutExerciseInfo } from './ImmersiveWorkoutExerciseInfo';
import { ImmersiveWorkoutControls } from './ImmersiveWorkoutControls';
import { ImmersiveWorkoutBreakOverlay } from './ImmersiveWorkoutBreakOverlay';
import { ImmersiveWorkoutAudioSettings } from './ImmersiveWorkoutAudioSettings';
import { AlertModal } from '../../../AlertModal';
import { EndWorkoutOverviewModal } from '../EndWorkoutOverviewModal';
import { loadRandomAudio, loadNextExerciseOrSetAudio } from '../../../../Store/textToSpeech';
import { getMediaUriObject } from '../../../../Model/Media/Media';
import { ConversationalWorkoutCoach } from '../../../../Services/AI/ConversationalWorkoutCoach';

const Wrapper = styled.div<{ isFullscreen?: boolean }>`
  ${({ isFullscreen }) =>
    isFullscreen
      ? `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
  `
      : `
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
  `}

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0;
    max-width: none;
    z-index: 1000;
  }
`;

const Container = styled.div<{ isFullscreen?: boolean }>`
  position: relative;
  width: 100%;
  height: ${({ isFullscreen }) => (isFullscreen ? '100vh' : 'calc(100vh - 120px)')};
  min-height: 500px;
  background: #000;
  overflow: hidden;
  border-radius: ${({ isFullscreen }) => (isFullscreen ? 0 : '20px')};
  box-shadow: ${({ isFullscreen }) => (isFullscreen ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.2)')};

  @media (max-width: 768px) {
    height: 100vh;
    min-height: 100vh;
    border-radius: 0;
    box-shadow: none;
  }
`;

const TopGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 220px;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.4) 25%,
    rgba(0, 0, 0, 0.1) 60%,
    transparent 100%
  );
  z-index: 10;
  pointer-events: none;
`;

const BottomGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 250px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%);
  z-index: 10;
  pointer-events: none;
`;

const HeaderContainer = styled.div<{ visible?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 250;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;

  & button, & [role="button"] {
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  }
`;

const BottomContainer = styled.div<{ visible?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;

  & button, & [role="button"] {
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  }
`;

type WorkoutPhase = 'countdown' | 'active' | 'break' | 'finished';

export type ImmersiveWorkoutProps = {
  onExit?: () => void;
};

// Simple audio test — run window.testAudio() in the browser console
(window as any).testAudio = () => {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    osc.frequency.value = 440;
    osc.connect(ctx.destination);
    osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); }, 500);
    console.info('[AudioTest] Playing 440Hz beep for 0.5s — do you hear it?');
  } catch (e) {
    console.error('[AudioTest] Failed:', e);
  }
};

export const ImmersiveWorkout: React.FC<ImmersiveWorkoutProps> = observer(({ onExit }) => {
  const { t } = useTrans();
  const { workoutViewStore, remoteSettings, language } = useRootStore();

  const [controlsVisible, setControlsVisible] = useState(true);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // --- Audio unlock: browsers block autoplay unless triggered by user gesture ---
  // We create a shared AudioContext and unlock it on first user click (Start button).
  // Then all TTS audio plays through fetch+decodeAudioData on this unlocked context.
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const audioUnlockedRef = React.useRef(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const unlockAudio = useCallback(() => {
    if (audioUnlockedRef.current) return;
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = ctx;
      // Play a tiny silent buffer to fully unlock audio output
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
      audioUnlockedRef.current = true;
      setAudioUnlocked(true);
    } catch { /* AudioContext not supported */ }
  }, []);

  const playAudioUrl = useCallback(async (url: string): Promise<void> => {
    console.info('[ImmersiveWorkout] playAudioUrl', url.substring(0, 80));
    try {
      const a = new Audio(url);
      await a.play();
      console.info('[ImmersiveWorkout] Audio playing');
    } catch (e) {
      console.warn('[ImmersiveWorkout] playAudioUrl failed:', e);
    }
  }, []);

  // Cleanup AudioContext on unmount
  useEffect(() => {
    return () => {
      audioContextRef.current?.close().catch(() => {});
      audioContextRef.current = null;
    };
  }, []);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  }, []);

  const viewWorkout = workoutViewStore.viewWorkout;
  const selectedViewSet = viewWorkout?.selectedViewSet;
  const isAutoMode = viewWorkout?.autoPlayEnabled ?? false;
  const isAudioEnabled = remoteSettings.get('audioFeedback') !== false;
  const isAICoachActive = remoteSettings.get('aiCoachEnabled') !== false && isAudioEnabled;

  // --- AI Coach: conversational workout coaching ---
  const aiCoachRef = React.useRef<ConversationalWorkoutCoach | null>(null);
  // Tracks whether the coach is actually running — used to gate fallback audio
  const [aiCoachRunning, setAiCoachRunning] = useState(false);

  useEffect(() => {
    if (!viewWorkout || !isAICoachActive) {
      aiCoachRef.current?.dispose();
      aiCoachRef.current = null;
      setAiCoachRunning(false);
      return;
    }
    const coach = new ConversationalWorkoutCoach(
      viewWorkout, language.language, remoteSettings,
    );
    aiCoachRef.current = coach;
    coach.start();
    setAiCoachRunning(true);
    return () => { coach.dispose(); aiCoachRef.current = null; setAiCoachRunning(false); };
  }, [viewWorkout, language.language, isAICoachActive, remoteSettings]);

  // Calculate next set
  const nextSet = React.useMemo(() => {
    if (!selectedViewSet) return undefined;
    if (selectedViewSet.viewBlock.completed) {
      return viewWorkout?.nextViewSetFromEnd;
    }
    return selectedViewSet.nextSet;
  }, [selectedViewSet, viewWorkout]);

  // Check if this is the last exercise
  const isLastExercise = React.useMemo(() => {
    if (!selectedViewSet || !viewWorkout) return false;
    const blocks = viewWorkout.exerciseBlocks;
    const currentBlockIndex = blocks.findIndex((b) => b.id === selectedViewSet.viewBlock.id);
    return currentBlockIndex === blocks.length - 1;
  }, [selectedViewSet, viewWorkout]);

  // Calculate progress
  const progress = React.useMemo(() => {
    if (!selectedViewSet || !viewWorkout) return 0;
    const totalExercises = viewWorkout.exerciseBlocks.length;
    const currentIndex = viewWorkout.exerciseBlocks.findIndex((b) => b.id === selectedViewSet.viewBlock.id) + 1;
    return totalExercises > 0 ? currentIndex / totalExercises : 0;
  }, [selectedViewSet, viewWorkout]);

  // Use MobX computed phase from ViewWorkout - this automatically tracks dependencies
  // Override with 'finished' when end workout modal is visible
  const endWorkoutModalVisible = workoutViewStore.endWorkoutOverviewModalVisible;
  const phase: WorkoutPhase = endWorkoutModalVisible ? 'finished' : (viewWorkout?.currentPhase ?? 'countdown');

  // Auto-stop exercise when planned duration is reached in auto mode
  useEffect(() => {
    if (!isAutoMode || !selectedViewSet) return;
    const disposer = reaction(
      () => selectedViewSet.duration,
      (durationMs) => {
        if (
          selectedViewSet.logSet?.isRunning &&
          selectedViewSet.plannedDurationMs > 0 &&
          durationMs >= selectedViewSet.plannedDurationMs
        ) {
          const stoppedSet = selectedViewSet.stop();
          if (stoppedSet && viewWorkout) {
            viewWorkout.workoutLog.save().catch(() => {
              // Silently handle save errors
            });
            // If no next set, this is the last exercise — end workout
            if (!nextSet) {
              workoutViewStore.stopWorkout();
            }
          }
        }
      },
    );
    return () => disposer();
  }, [selectedViewSet, isAutoMode, viewWorkout, workoutViewStore, nextSet]);

  // --- Audio feedback for immersive workout ---
  // Store preloaded audio URIs (not Audio elements) to avoid autoplay restrictions
  const breakAudioUriRef = React.useRef<string | null>(null);
  const breakAudioTriggeredRef = React.useRef(false);

  // Always preload break audio URI when exercise changes (no aiCoachRunning gate here —
  // gating only the trigger prevents the race where cleanup clears the URI before use)
  useEffect(() => {
    if (!isAudioEnabled || !selectedViewSet) return;
    breakAudioTriggeredRef.current = false;
    breakAudioUriRef.current = null;
    loadRandomAudio(['break_1', 'break_2'], {}, language.language)
      .then((media) => {
        if (media) {
          const uri = getMediaUriObject(media).uri;
          if (uri) {
            breakAudioUriRef.current = uri;
          }
        }
      })
      .catch((e) => console.warn('[ImmersiveWorkout] Break audio preload failed:', e));
    return () => {
      breakAudioUriRef.current = null;
    };
  }, [selectedViewSet, isAudioEnabled, language]);

  // Trigger break audio before exercise ends (timed ~3s before end)
  useEffect(() => {
    if (aiCoachRunning) return;
    if (!isAudioEnabled || !selectedViewSet || !isAutoMode) return;
    const disposer = reaction(
      () => selectedViewSet.remainingDuration,
      (remainingMs) => {
        if (breakAudioTriggeredRef.current) return;
        // Play break cue ~3 seconds before exercise ends
        if (remainingMs / 1000 <= 3 && remainingMs > 0) {
          breakAudioTriggeredRef.current = true;
          const uri = breakAudioUriRef.current;
          if (uri) {
            playAudioUrl(uri);
          }
        }
      },
    );
    return () => disposer();
  }, [selectedViewSet, isAudioEnabled, isAutoMode, playAudioUrl, aiCoachRunning]);

  // Play next exercise TTS announcement when entering break phase
  useEffect(() => {
    if (aiCoachRunning) return;
    if (phase !== 'break' || !isAudioEnabled || !nextSet) return;
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (cancelled) return;
      loadNextExerciseOrSetAudio(nextSet, language.language)
        .then((media) => {
          if (cancelled) return;
          if (media) {
            const uri = getMediaUriObject(media).uri;
            if (uri) {
              playAudioUrl(uri);
            } else {
              console.warn('[ImmersiveWorkout] Next exercise TTS media has no URL');
            }
          } else {
            console.warn('[ImmersiveWorkout] Next exercise TTS returned no media');
          }
        })
        .catch((e) => console.warn('[ImmersiveWorkout] Next exercise TTS failed:', e));
    }, 1500); // Delay so break audio finishes first
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [phase, nextSet, isAudioEnabled, language, playAudioUrl, aiCoachRunning]);

  const handleToggleControls = useCallback(() => {
    unlockAudio(); // Also unlock on any tap/click during workout
    setControlsVisible((prev) => !prev);
  }, [unlockAudio]);

  const handleStartWorkout = useCallback(() => {
    unlockAudio(); // Unlock AudioContext on user gesture (Start button click)
    workoutViewStore.prepareWorkoutStart();
    // Phase automatically updates via MobX computed currentPhase
  }, [workoutViewStore, unlockAudio]);

  const handleToggleMode = useCallback(() => {
    if (isAutoMode) {
      viewWorkout?.disableAutoPlay();
    } else {
      viewWorkout?.enableAutoPlay();
    }
  }, [isAutoMode, viewWorkout]);

  const handleSkipToNext = useCallback(() => {
    unlockAudio(); // Ensure audio stays unlocked on user interaction
    if (nextSet) {
      viewWorkout?.selectViewSet(nextSet);
      workoutViewStore.startSet(nextSet);
      // Phase will automatically update to 'active' via MobX when set starts
    }
  }, [nextSet, viewWorkout, workoutViewStore, unlockAudio]);

  const handleStopSet = useCallback(() => {
    if (!selectedViewSet || !viewWorkout) {
      return;
    }

    // If set is already finished, skip to next instead of trying to stop again
    if (selectedViewSet.isFinished && nextSet) {
      handleSkipToNext();
      return;
    }

    // If set hasn't started yet (manual mode), start it first before stopping
    if (!selectedViewSet.isStarted) {
      workoutViewStore.startSet(selectedViewSet);
    }

    // Now stop the set
    if (selectedViewSet.isRunning || selectedViewSet.isStarted) {
      const stoppedSet = selectedViewSet.stop();

      if (stoppedSet) {
        // Save the workout log after stopping set
        viewWorkout.workoutLog.save().catch(() => {
          // Silently handle save errors - workout will retry on next action
        });

        // Check if there's a next set - phase will auto-update to 'break' via MobX
        if (!nextSet) {
          // Last exercise - show end workout modal
          workoutViewStore.stopWorkout();
        }
      }
    }
  }, [selectedViewSet, nextSet, viewWorkout, workoutViewStore, handleSkipToNext]);

  const handleEndWorkout = useCallback(() => {
    workoutViewStore.stopWorkout();
  }, [workoutViewStore]);

  const handleConfirmClose = useCallback(() => {
    setShowConfirmClose(true);
  }, []);

  const handleDiscardAndExit = useCallback(() => {
    setShowConfirmClose(false);
    workoutViewStore.stopWorkout();
    onExit?.();
  }, [workoutViewStore, onExit]);

  if (!viewWorkout) {
    return null;
  }

  // Countdown phase
  if (phase === 'countdown') {
    return (
      <Wrapper isFullscreen={isFullscreen}>
        <Container ref={containerRef} isFullscreen={isFullscreen}>
          <ImmersiveWorkoutVideoBackground
            viewSet={viewWorkout.firstViewBlock?.firstSet}
            onPress={handleToggleControls}
            allowSound={audioUnlocked}
          />
          <ImmersiveWorkoutCountdown
            viewWorkout={viewWorkout}
            isAutoMode={isAutoMode}
            isAudioEnabled={isAudioEnabled}
            onStart={handleStartWorkout}
            onClose={onExit}
            onToggleMode={handleToggleMode}
            onOpenSettings={() => setSettingsVisible(true)}
            onToggleFullscreen={handleToggleFullscreen}
            isFullscreen={isFullscreen}
          />
          {settingsVisible && <ImmersiveWorkoutAudioSettings onClose={() => setSettingsVisible(false)} />}
        </Container>
      </Wrapper>
    );
  }

  // Finished phase
  if (phase === 'finished') {
    return (
      <Wrapper isFullscreen={isFullscreen}>
        <Container ref={containerRef} isFullscreen={isFullscreen}>
          <EndWorkoutOverviewModal />
        </Container>
      </Wrapper>
    );
  }

  // Active or Break phase
  return (
    <Wrapper isFullscreen={isFullscreen}>
      <Container ref={containerRef} isFullscreen={isFullscreen}>
      {/* Fullscreen video background */}
      <ImmersiveWorkoutVideoBackground
        viewSet={selectedViewSet}
        onPress={handleToggleControls}
        allowSound={audioUnlocked}
      />

      {/* Cinematic gradients */}
      <TopGradient />
      <BottomGradient />

      {/* Floating header */}
      <HeaderContainer visible={controlsVisible}>
        <ImmersiveWorkoutHeader
          progress={progress}
          isAutoMode={isAutoMode}
          isAudioEnabled={isAudioEnabled}
          isFullscreen={isFullscreen}
          onClose={handleConfirmClose}
          onOpenSettings={() => setSettingsVisible(true)}
          onToggleMode={handleToggleMode}
          onToggleFullscreen={handleToggleFullscreen}
        />
      </HeaderContainer>

      {/* Break overlay */}
      {phase === 'break' && selectedViewSet && nextSet && (
        <ImmersiveWorkoutBreakOverlay
          viewSet={selectedViewSet}
          nextSet={nextSet}
          isAutoMode={isAutoMode}
          onSkipBreak={handleSkipToNext}
        />
      )}

      {/* Bottom controls */}
      <BottomContainer visible={controlsVisible && phase === 'active'}>
        {selectedViewSet && (
          <>
            <ImmersiveWorkoutExerciseInfo
              viewSet={selectedViewSet}
              isDurationBased={selectedViewSet.durationBased || isAutoMode}
            />
            <ImmersiveWorkoutControls
              viewSet={selectedViewSet}
              isLastExercise={isLastExercise}
              onNext={handleStopSet}
              onStop={handleEndWorkout}
            />
          </>
        )}
      </BottomContainer>

      {/* Modals */}
      {settingsVisible && <ImmersiveWorkoutAudioSettings onClose={() => setSettingsVisible(false)} />}
      {showConfirmClose && (
        <AlertModal
          isOpen={showConfirmClose}
          title={t('endWorkoutAlert.title', { defaultValue: 'End workout' })}
          body={t('endWorkoutAlert.body', { defaultValue: 'Are you sure you want to end this workout?' })}
          confirmText={t('endWorkoutAlert.confirm', { defaultValue: 'End workout' }) ?? 'End workout'}
          cancelText={t('endWorkoutAlert.continueWorkout', { defaultValue: 'Continue workout' }) ?? 'Continue'}
          onCancel={() => setShowConfirmClose(false)}
          onConfirm={handleDiscardAndExit}
        />
      )}
      </Container>
    </Wrapper>
  );
});
